import { DocumentRow } from "../components/Docs/DocumentCard";

/**
 * Filtering and ordering for the documents home — the same rules whichever way the list is drawn.
 *
 * ONE IMPLEMENTATION FOR BOTH VIEWS, which is the point of it being here rather than in either.
 * The grid and the list are two renderings of one list; a filter that narrowed the cards and not
 * the rows, or a sort the table did its own way, would be the same defect twice over.
 *
 * APPLIED IN THE BROWSER, over the rows the page already holds -- see FETCH_LIMIT in Documents.tsx
 * for how many that is and what bounds it. Stated plainly rather than hidden: the counts beside
 * each filter describe the documents that were fetched. Past the cap the list is already showing a
 * subset, and counts from a separate query would disagree with the list they sit beside, which is
 * worse than a count that matches what is on screen. The answer there is server-side paging for the
 * list and the counts together, not a second source for the numbers.
 */

/** Display label -> the Status value stored on the revision, in t1's order: live states, then terminal. */
export const DOCUMENT_STATUSES: {label: string; status: string}[] = [
    {label: "Draft", status: "DRAFT"},
    {label: "Released", status: "RELEASED"},
    {label: "Deprecated", status: "DEPRECATED"},
    {label: "Superseded", status: "SUPERSEDED"},
    {label: "Cancelled", status: "CANCELLED"},
    {label: "Void", status: "VOID"},
];

export type DocumentFilters = {
    statuses: Set<string>;
    /** OBJECT_TYPE acronyms -- SOP, WI, VAL. What the number is built from and what people say. */
    types: Set<string>;
    /** Created within the last week. */
    newestOnly: boolean;
    daysOldEnabled: boolean;
    /** Held as typed, so a half-entered "" or "0" reaches here; this module decides what is usable. */
    daysOld: string;
};

export const NO_FILTERS: DocumentFilters = {
    statuses: new Set(), types: new Set(), newestOnly: false, daysOldEnabled: false, daysOld: "",
};

export function anyFilterActive(filters: DocumentFilters): boolean {
    return filters.statuses.size > 0 || filters.types.size > 0
        || filters.newestOnly || filters.daysOldEnabled;
}

/**
 * The creation cutoff the two recency controls resolve to, or null.
 *
 * ONE CONDITION, NOT TWO. Newest (the last week) and Days old (the last N days) both cut on the
 * same column, so AND-ing them is simply the later of the two cutoffs -- the tighter window wins,
 * rather than the rows vanishing when somebody ticks both.
 */
function createdAfter(filters: DocumentFilters): number | null {
    const cutoffs: number[] = [];
    if (filters.newestOnly) cutoffs.push(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const days = parseInt(filters.daysOld, 10);
    if (filters.daysOldEnabled && Number.isFinite(days) && days > 0) {
        cutoffs.push(Date.now() - days * 24 * 60 * 60 * 1000);
    }
    return cutoffs.length > 0 ? Math.max(...cutoffs) : null;
}

export function applyFilters(rows: DocumentRow[], filters: DocumentFilters): DocumentRow[] {
    const cutoff = createdAfter(filters);
    return rows.filter(row => {
        if (filters.statuses.size > 0 && !filters.statuses.has(row.Status ?? "")) return false;
        if (filters.types.size > 0 && !filters.types.has(row.ObjectTypeAcronym ?? "")) return false;
        if (cutoff !== null) {
            // A ROW WITH NO CREATION DATE IS NOT RECENT. Treating a missing date as "now" would put
            // it at the top of every recency filter, which is where the newest thing belongs and it
            // is not the newest thing -- it is a row we know nothing about.
            const created = row.CreatedDatetime ? Date.parse(row.CreatedDatetime) : NaN;
            if (!Number.isFinite(created) || created < cutoff) return false;
        }
        return true;
    });
}

export type DocumentStatistics = {
    statuses: {label: string; status: string; count: number}[];
    types: {acronym: string; name: string; count: number}[];
};

/**
 * The counts beside each filter row.
 *
 * COUNTED OVER THE UNFILTERED ROWS, so ticking Draft does not reduce every other count to zero and
 * leave the pane looking broken. It is what a filter pane is for: the numbers say what ticking a
 * box would give you, which they cannot do if they already reflect the boxes that are ticked.
 */
export function statisticsOf(rows: DocumentRow[]): DocumentStatistics {
    const types = new Map<string, {acronym: string; name: string; count: number}>();
    for (const row of rows) {
        const acronym = row.ObjectTypeAcronym;
        // A type with no acronym cannot be filtered to -- the acronym IS the filter value -- so it
        // is left out rather than offered as a blank row that does nothing.
        if (!acronym) continue;
        const found = types.get(acronym);
        if (found) found.count += 1;
        else types.set(acronym, {acronym, name: row.ObjectTypeName ?? "", count: 1});
    }

    return {
        statuses: DOCUMENT_STATUSES.map(one => ({
            ...one, count: rows.filter(row => row.Status === one.status).length,
        })),
        types: Array.from(types.values()).sort((a, b) => a.acronym.localeCompare(b.acronym)),
    };
}

/**
 * The orderings offered, each with the direction it reads best in to start with.
 *
 * DRAFTS FIRST IS THE DEFAULT and stays the default: a draft is the only revision anybody can edit
 * in this application, so it is what somebody arriving here is most likely to want.
 */
export type SortKey = "DRAFTS" | "ObjectID" | "Name" | "CreatedDatetime" | "LastModifiedDatetime";

export const SORT_OPTIONS: {key: SortKey; label: string; descending: boolean}[] = [
    {key: "DRAFTS", label: "Drafts first", descending: false},
    {key: "ObjectID", label: "Object ID", descending: false},
    {key: "Name", label: "Name", descending: false},
    // NEWEST FIRST for both dates, because "when was this created" is almost always asked as
    // "what is new". Reversible, for the one time it is not.
    {key: "CreatedDatetime", label: "Created", descending: true},
    {key: "LastModifiedDatetime", label: "Last modified", descending: true},
];

/**
 * PlatformTable's sort specification, as the key and direction this module sorts by.
 *
 * THE TABLE SPEAKS IN SortAttribute AND Direction, because that is what every applet in the suite
 * declares and what the remembered-sort machinery stores. This page thinks in SortKey. One
 * translation, here, next to the comparator it feeds -- rather than the table's vocabulary leaking
 * into the page and the page's into the table.
 *
 * An unrecognised attribute is null rather than a guess: the fallback "Row Identity" selection is
 * one PlatformTable adds itself and no document carries, and sorting by it would quietly order the
 * list by nothing.
 */
export function sortFromSpecification(
    specification: { SortAttribute?: string; Direction?: string }[] | null | undefined,
): { key: SortKey; descending: boolean } | null {
    const first = (specification ?? [])[0];
    if (!first?.SortAttribute) return null;
    if (!SORT_OPTIONS.some(option => option.key === first.SortAttribute)) return null;
    return {
        key: first.SortAttribute as SortKey,
        descending: String(first.Direction ?? "").toUpperCase() === "DESC",
    };
}

export function applySort(rows: DocumentRow[], key: SortKey, descending: boolean): DocumentRow[] {
    const sorted = [...rows];

    if (key === "DRAFTS") {
        sorted.sort((a, b) => Number(b.Status === "DRAFT") - Number(a.Status === "DRAFT"));
        return descending ? sorted.reverse() : sorted;
    }

    const direction = descending ? -1 : 1;
    sorted.sort((a, b) => {
        const left = (a as any)[key];
        const right = (b as any)[key];
        // MISSING SORTS LAST IN BOTH DIRECTIONS. A document with no number yet is not "earliest";
        // putting it atop an ascending sort reads as a value nobody can see.
        const leftEmpty = left === null || left === undefined || left === "";
        const rightEmpty = right === null || right === undefined || right === "";
        if (leftEmpty || rightEmpty) return leftEmpty ? (rightEmpty ? 0 : 1) : -1;

        if (key === "CreatedDatetime" || key === "LastModifiedDatetime") {
            return (Date.parse(left) - Date.parse(right)) * direction;
        }
        // NUMERIC COLLATION, so SOP-00009 sorts before SOP-00010 rather than after it -- the
        // numbers are zero-padded today, and this stops the order changing if they ever are not.
        return String(left).localeCompare(String(right), undefined,
                                          {numeric: true, sensitivity: "base"}) * direction;
    });
    return sorted;
}
